/**
 * @file cc-predictive.js
 * Predictive dropdown feature module for the Credit Calculator Wizards pages.
 *
 * Responsibilities:
 *  - Dropdown state helpers — `showDropdown`, `hideDropdown`, `setDropdownLoading`,
 *    `setDropdownHint`, `setDropdownEmpty`.
 *  - `ensureSpinKeyframes` IIFE — injects a CSS `@keyframes spin` rule if absent.
 *  - Delegated `.predictive` event handlers — focusin (immediate hint/fetch),
 *    input (debounced fetch), paste (blocked).
 *  - `hideOtherList` / `hideList` — collapse sibling dropdowns and manage focus-out behaviour.
 *  - Dropdown item click handler — sets the selected value, clears downstream dependant
 *    fields, and advances focus to the next input.
 *  - `ccFetchPredictive` — builds and dispatches the AJAX request with stale-request
 *    cancellation and a race-guard token.
 *  - `ccBuildLabelForInput` — derives the display label from a result item based on
 *    the input type.
 *  - `ccUpdateDropdown` — normalises the server response, highlights query matches,
 *    and renders the `<li>` items.
 */

import { checkUndefined } from '../utils/_string.js';


/**
 * Shows the dropdown menu associated with a predictive input.
 *
 * @param {jQuery} $input The predictive text input element.
 */
function showDropdown($input) {
    $input
        .siblings(".dropdown-menu")
        .removeClass("hide-element")
        .addClass("display-element");
}

/**
 * Hides the dropdown menu associated with a predictive input.
 *
 * @param {jQuery} $input The predictive text input element.
 */
function hideDropdown($input) {
    $input
        .siblings(".dropdown-menu")
        .removeClass("display-element")
        .addClass("hide-element");
}
/**
 * Replaces the dropdown list with a loading spinner and shows the dropdown.
 *
 * @param {jQuery} $input   The predictive text input element.
 * @param {string} [message="Searching\u2026"] Text to show alongside the spinner.
 */
function setDropdownLoading($input, message = "Searching…") {
    var $ul = $input.siblings("ul");
    $ul.empty().append(
        `<li class="loading margin-bottom-0"><span class="spinner"></span>${message}</li>`,
    );
    showDropdown($input);
}
/**
 * Replaces the dropdown list with a hint message and shows the dropdown.
 * Used when the input is focused but no query has been entered yet.
 *
 * @param {jQuery} $input   The predictive text input element.
 * @param {string} [message="Start typing to see suggestions\u2026"] Hint text to display.
 */
function setDropdownHint($input, message = "Start typing to see suggestions…") {
    var $ul = $input.siblings("ul");
    $ul.empty().append(`<li class="hint">${message}</li>`);
    showDropdown($input);
}

/**
 * Replaces the dropdown list with an empty-results message and shows the dropdown.
 * Falls back to the input's `empty` attribute, then a generic string.
 *
 * @param {jQuery} $input    The predictive text input element.
 * @param {string} [message] Override message; falls back to `$input.attr("empty")`.
 */
function setDropdownEmpty($input, message) {
    var $ul = $input.siblings("ul");
    var text = message || $input.attr("empty") || "No results found…";
    $ul.empty().append(`<li class="empty">${text}</li>`);
    showDropdown($input);
}

/**
 * Spin keyframes injection (CSS fallback).
 * Inserts a `@keyframes spin` rule into the document `<head>` if one is not already present.
 */
(function ensureSpinKeyframes() {
    var id = "spin-keyframes";
    if (!document.getElementById(id)) {
        var style = document.createElement("style");
        style.id = id;
        style.textContent = "@keyframes spin { to { transform: rotate(360deg); } }";
        document.head.appendChild(style);
    }
})();

/**
 * Predictive debounce & request state.
 */
var typingTimer;
var doneTypingInterval = 300; // ms
var ccPredictiveActiveXhr = new Map(); // key: DOM node, value: jqXHR
var ccPredictiveReqToken = 0; // race-guard token

/**
 * Delegated `.predictive` event handlers.
 * - **focusin**: shows a hint immediately, or fires a fetch if a query is already present.
 * - **input**: debounces a fetch by `doneTypingInterval` ms.
 * - **paste**: blocked — users must type into predictive fields.
 */

/** Focus in: show dropdown immediately. */
$('body').on("focusin", ".predictive", function (e) {
    var $input = $(e.currentTarget);

    if ($input.hasClass("predictive-dependant")) {
        var parentSel = $input.attr("dependantParent");
        var parentVal = checkUndefined($(parentSel).attr("value"));
        if (!parentVal) {
            setDropdownHint($input, "Select the previous field first…");
            return;
        }
    }

    var q = $($input.attr("requestElementDescription")).val() || "";
    if (q.length === 0) {
        setDropdownHint($input, "Start typing to see suggestions…");
    } else {
        setDropdownLoading($input);
        ccFetchPredictive($input);
    }

    window.setTimeout(hideOtherList, 200, $input);
});

/** Input change: debounce fetch. */
$('body').on("input", ".predictive", function (e) {
    clearTimeout(typingTimer);
    var $input = $(e.currentTarget);

    if ($input.hasClass("predictive-dependant")) {
        var parentSel = $input.attr("dependantParent");
        var parentVal = checkUndefined($(parentSel).attr("value"));
        if (!parentVal) {
            setDropdownHint($input, "Select the previous field first…");
            return;
        }
    }

    setDropdownLoading($input);
    typingTimer = setTimeout(function () {
        ccFetchPredictive($input);
    }, doneTypingInterval);
});

/** Block paste into predictive fields. */
$('body').on("paste", ".predictive", function (e) {
    e.preventDefault();
    e.stopPropagation();
    alert("You can't paste into this field, please type instead.");
    e.returnValue = false;
});

/**
 * Show/hide dropdown lists and manage focus-out behaviour.
 */

var hideForm = false;

/**
 * Hides all open dropdown menus except the one sibling to `list`.
 * Used after focusin to collapse any other open dropdowns.
 *
 * @param {jQuery} list The currently focused predictive input.
 */
function hideOtherList(list) {
    $("body")
        .find(".dropdown-menu")
        .each(function (index) {
            if (
                index !==
                $("body")
                    .find(".dropdown-menu")
                    .index($(list).siblings(".dropdown-menu"))
            ) {
                $(this).removeClass("display-element").addClass("hide-element");
            }
        });
}

/**
 * Hides all open predictive dropdowns when focus has left the field.
 * Guarded by `hideForm` so that clicking a list item does not prematurely close it.
 */
function hideList() {
    if (hideForm) {
        if ($(".predictive ~ .dropdown-menu").hasClass("display-element")) {
            $(".predictive ~ .dropdown-menu")
                .removeClass("display-element")
                .addClass("hide-element");
        } else {
            $(".predictive ~ .dropdown-menu")
                .siblings(".dropdown-menu")
                .removeClass("display-element")
                .addClass("hide-element");
        }
    }
}

$("body").on(
    {
        focusin: function () {
            hideForm = false;
        },
        focusout: function () {
            hideForm = true;
            window.setTimeout(hideList, 200);
        },
    },
    ".predictive ~ .dropdown-menu li a",
);

$("body").on(
    {
        focusout: function () {
            hideForm = true;
            window.setTimeout(hideList, 200);
        },
    },
    ".predictive",
);

/**
 * Delegated click handler for dropdown list items.
 * Sets the visible input value and the hidden target element's `value` attribute,
 * clears any downstream dependant fields, hides the dropdown, and advances focus.
 */
$('body').on(
    {
        click: function (e) {
            e.preventDefault();

            // Set the visible text to the chosen label
            $(this).parents(".dropdown").find(".predictive").val($(this).text());

            // Set the code/id into the target hidden element's `value` attribute
            $(
                $(this).parents(".dropdown").find(".predictive").attr("targetElementID"),
            ).attr("value", $(this).parent().attr("elementID"));

            // Hide the dropdown and clear any error styles
            $(this)
                .closest(".dropdown-menu")
                .removeClass("display-element")
                .addClass("hide-element");
            $(e.currentTarget).closest("div").removeClass("error");

            // Clear downstream dependant inputs
            var inputIndex =
                $(this)
                    .closest("form")
                    .find("input[type=text], :submit")
                    .index(
                        $(this).parents(".dropdown").find("input[type=text], :submit"),
                    ) + 1;

            for (
                var index = inputIndex;
                index <= $(this).closest("form").find("input[type=text], :submit").length;
                index++
            ) {
                var $field = $(this)
                    .closest("form")
                    .find("input[type=text], :submit")
                    .eq(index);
                if ($field.hasClass("predictive-dependant")) {
                    $field.val("");
                }
            }

            // Move focus to the next input or submit
            $(this)
                .closest("form")
                .find("input[type=text], :submit")
                .eq(inputIndex)
                .focus();
        },
    },
    ".predictive ~ .dropdown-menu li a, .predictive-dependant ~ .dropdown-menu li a",
);

/**
 * Builds and dispatches the AJAX predictive search request for the given input.
 * Cancels any in-flight request for that input before sending a new one.
 * Uses a monotonic token to discard responses that arrive out of order.
 *
 * @param {jQuery} $input The predictive text input element.
 */
function ccFetchPredictive($input) {
    if ($input.hasClass("predictive-dependant")) {
        var parentSel = $input.attr("dependantParent");
        var parentVal = checkUndefined($(parentSel).attr("value"));
        if (!parentVal) {
            setDropdownHint($input, "Select the previous field first…");
            return;
        }
    }

    // Extra arguments built from requestArguments JSON
    var extraArgs = "";
    var argAttr = checkUndefined($input.attr("requestArguments"));
    if (argAttr !== "") {
        try {
            var stack = $.parseJSON(argAttr);
            for (var i = 0; i < stack.length; i++) {
                var depVal = $(stack[i].dependantParent).attr(stack[i].attribute);
                extraArgs += `&${stack[i].attribute}=${encodeURIComponent(depVal || "")}`;
            }
        } catch (err) {
            // malformed JSON, ignore
        }
    }

    var descSel = $input.attr("requestElementDescription");
    var q = checkUndefined($(descSel).val());
    var base = $input.attr("callbackRequest") || "";
    var idParam = $input.hasClass("predictive-dependant")
        ? `id=${encodeURIComponent(checkUndefined($($input.attr("dependantParent")).attr("value")))}`
        : "";
    var url = `${base}${idParam}${idParam ? "&" : ""}description=${encodeURIComponent(q)}${extraArgs}`;

    // Cancel any in-flight request for this input
    var key = $input.get(0);
    var prevXhr = ccPredictiveActiveXhr.get(key);
    if (prevXhr && prevXhr.readyState !== 4) {
        try {
            prevXhr.abort();
        } catch (e) {}
    }

    var token = ++ccPredictiveReqToken;
    setDropdownLoading($input);

    var xhr = $.get(url)
        .done(function (data) {
            if (token !== ccPredictiveReqToken) return;
            ccUpdateDropdown($input, data);
        })
        .fail(function () {
            if (token !== ccPredictiveReqToken) return;
            setDropdownEmpty($input, "No results found…");
        })
        .always(function () {
            ccPredictiveActiveXhr.delete(key);
        });

    ccPredictiveActiveXhr.set(key, xhr);
}

/**
 * Derives the display label for a single result item based on the input being populated.
 * - `#whereDidYouStudy`: institution name only (no code prefix).
 * - Dependant inputs: `CODE - text` if both are present.
 * - All others: description/name text, falling back to code.
 *
 * @param {jQuery} $input The predictive text input element.
 * @param {Object} item   A single result object from the server (may have `code`/`id` and `description`/`name`).
 * @returns {string} The display label, or an empty string if no usable value is found.
 */
function ccBuildLabelForInput($input, item) {
    var code =
        typeof item.code !== "undefined" && item.code !== null
            ? String(item.code)
            : typeof item.id !== "undefined" && item.id !== null
              ? String(item.id)
              : "";

    var text =
        typeof item.description !== "undefined" &&
        item.description !== null &&
        item.description !== ""
            ? String(item.description)
            : typeof item.name !== "undefined" && item.name !== null
              ? String(item.name)
              : "";

    if ($input.is("#whereDidYouStudy")) {
        return text || code;
    }

    if ($input.hasClass("predictive-dependant")) {
        if (code && text) return `${code} - ${text}`;
        return code || text;
    }

    return text || code;
}

/**
 * Normalises the server response, renders `<li>` items into the dropdown, and
 * highlights the characters matching the current input value.
 * Accepts an array, a JSON string, or a plain object keyed by arbitrary keys.
 * Falls back to {@link setDropdownEmpty} if no renderable items are found.
 *
 * @param {jQuery}               $input   The predictive text input element.
 * @param {Array|Object|string}  contents The raw server response.
 */
function ccUpdateDropdown($input, contents) {
    var $ul = $input.siblings("ul");
    $ul.empty();

    var normalized = [];
    if (Array.isArray(contents)) {
        normalized = contents;
    } else if (typeof contents === "string" && contents !== "") {
        try {
            var parsed = JSON.parse(contents);
            if (Array.isArray(parsed)) {
                normalized = parsed;
            } else if (parsed && typeof parsed === "object") {
                normalized = Object.keys(parsed).map(function (key) {
                    return parsed[key];
                });
            }
        } catch (e) {
            normalized = [];
        }
    } else if (contents && typeof contents === "object") {
        normalized = Object.keys(contents).map(function (key) {
            return contents[key];
        });
    }

    if (!normalized.length) {
        setDropdownEmpty($input);
        return;
    }

    var rawQ = $input.val() || "";
    var safeQ = rawQ.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, "\\$&");
    var rx = safeQ ? new RegExp(safeQ, "ig") : null;

    normalized.forEach(function (e) {
        var elementId =
            typeof e.code !== "undefined" && e.code !== null
                ? String(e.code)
                : typeof e.id !== "undefined" && e.id !== null
                  ? String(e.id)
                  : "";

        var label = ccBuildLabelForInput($input, e);
        if (!label) return;

        var highlighted = rx
            ? label.replace(rx, function (str) {
                  return `<b>${str}</b>`;
              })
            : label;

        $ul.append(
            `<li elementID="${elementId}" class="margin-bottom-0"><a href="">${highlighted}</a></li>`,
        );
    });

    if (!$ul.children().length) {
        setDropdownEmpty($input);
    } else {
        showDropdown($input);
    }
}
